import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { hrefFor } from '../lib/router.js';
import { ConfirmDialog, ErrorState, LoadingState, Panel, StatusBadge } from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import type { Screen } from '@hub/shared';

interface Item {
  key: string;
  label: string;
  state: 'ok' | 'warn' | 'todo';
  detail: string;
  screen: Screen;
  where: string;
}
interface Checklist {
  items: Item[];
  environment: string | null;
  preparedAt: string | null;
}
interface Prepared {
  templatesPublished: number;
  txShadow: string[];
  demo: { events: number; contacts: number; races: string[]; problems: string[] };
  notes: string[];
}

const BADGE: Record<Item['state'], { tone: Tone; text: string }> = {
  ok: { tone: 'ok', text: 'v pořádku' },
  warn: { tone: 'warn', text: 'pozor' },
  todo: { tone: 'neu', text: 'zbývá' },
};

/**
 * „Zkouška: kontrolní seznam“ a tlačítko „Připravit zkoušku“ (D-224). Seznam je živý (jen čtení);
 * příprava je bezpečná a opakovatelná, běží jen ve Zkoušce a nic neposílá.
 */
export function TrialChecklist(): JSX.Element {
  const client = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [result, setResult] = useState<Prepared | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list = useQuery<Checklist>({
    queryKey: ['trial-checklist'],
    queryFn: () => api.get('/api/send-tests/trial/checklist'),
    refetchInterval: 30_000,
  });
  const prepare = useMutation({
    mutationFn: () => api.post<Prepared>('/api/send-tests/trial/prepare', {}),
    onSuccess: (r) => {
      setAsking(false);
      setError(null);
      setResult(r);
      void client.invalidateQueries();
    },
    onError: (err: Error) => {
      setAsking(false);
      setError(err.message);
    },
  });

  const data = list.data;
  const staging = data?.environment === 'staging';
  const done = data ? data.items.filter((i) => i.state === 'ok').length : 0;
  return (
    <Panel
      title="Zkouška: kontrolní seznam"
      subtitle="Co je pro zkoušku s kolegy hotové a co zbývá. Seznam se obnovuje sám a nic nemění."
      action={
        <button
          type="button"
          className="btn pri"
          disabled={!staging || prepare.isPending}
          title={staging ? undefined : 'Příprava funguje jen v prostředí Zkouška.'}
          onClick={() => setAsking(true)}
        >
          Připravit zkoušku
        </button>
      }
    >
      {list.isLoading ? <LoadingState what="kontrolní seznam" /> : null}
      {list.isError ? (
        <ErrorState
          error={list.error}
          title="Kontrolní seznam se nepodařilo načíst"
          onRetry={() => void list.refetch()}
        />
      ) : null}
      {error ? <ErrorState error={new Error(error)} title="Příprava se nepovedla" /> : null}
      {result ? (
        <div className="confirm" role="status">
          <span>
            Zkouška je připravená: šablon {result.templatesPublished}, transakční kódy ve stínu{' '}
            {result.txShadow.length}, demo data {result.demo.contacts} kontaktů a{' '}
            {result.demo.races.length} závodů.
            {result.demo.problems.length > 0
              ? ` Pozor, některé demo události se nezpracovaly: ${result.demo.problems.join('; ')}`
              : ''}
          </span>
        </div>
      ) : null}
      {data ? (
        <>
          <p className="note" role="status">
            Hotovo {done} z {data.items.length}.
          </p>
          <ul className="s2c-runlist" data-testid="trial-checklist">
            {data.items.map((i) => (
              <li key={i.key}>
                <StatusBadge tone={BADGE[i.state].tone}>{BADGE[i.state].text}</StatusBadge>{' '}
                <b>{i.label}</b>
                <span className="note">
                  {' '}
                  {i.detail} <a href={hrefFor(i.screen)}>{i.where}</a>
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <ConfirmDialog
        open={asking}
        title="Připravit zkoušku?"
        confirmLabel="Připravit zkoušku"
        busy={prepare.isPending}
        onCancel={() => setAsking(false)}
        onConfirm={() => prepare.mutate()}
      >
        <p>
          Doplní značky, startovací šablony všech zpráv, přepínače odesílání pro zkušební kódy a
          demo data (zákazníci na adresách <code>@example.invalid</code> a na zkušebních adresách).
          Transakční zprávy se nastaví do stínu, nikdy do ostrého režimu.
        </p>
        <p>
          Zprávy smějí odejít jen na zkušební adresy; do SmartEmailingu se nic nezapisuje. Příprava
          je opakovatelná, nic nezdvojí.
        </p>
      </ConfirmDialog>
    </Panel>
  );
}
